import { FC, useEffect, useMemo } from 'react';
import { Box, Divider, IconButton, Tooltip, Typography } from '@mui/material';
import { KeyboardBackspace } from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { COLORS } from 'services/constants/colors';
import { PATHS } from 'routes/constants';
import { getSavedUrl } from 'redux/reducers/auth/selectors';
import {
	clearPpwrEmSettingsDetails,
	getPpwrEmSettingsRequest,
	getPpwrFeeOptionsRequest,
} from 'redux/reducers/ppwr/reducer';
import {
	getPpwrEmSettingsDetails,
	getPpwrEmSettingsDetailsLoading,
	getPpwrFeeOptions,
} from 'redux/reducers/ppwr/selectors';
import { EasParamsBox } from 'components/EMManagement/EMManagementId/EasParamsBox';
import { EMManagementIdHeader } from 'components/EMManagement/EMManagementId/EMManagementIdHeader';
import { ppwrSettingsBoxStructure } from './boxStructure';
import { PpwrSettingsEditDialog } from './PpwrSettingsEditDialog';
import { PackingProfile } from './PackingProfile';
import { CountryExclusions } from './CountryExclusions';
import { TPpwrEmSettingsView } from './types';

export const PpwrSettings: FC = () => {
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const { emId: emIdParam } = useParams<{ emId: string }>();
	const emId = Number(emIdParam);

	const savedUrl = useSelector(getSavedUrl);
	const settings = useSelector(getPpwrEmSettingsDetails);
	const settingsLoading = useSelector(getPpwrEmSettingsDetailsLoading);
	const feeOptions = useSelector(getPpwrFeeOptions);

	useEffect(() => {
		if (Number.isFinite(emId)) dispatch(getPpwrEmSettingsRequest({ emId }));
		return () => {
			dispatch(clearPpwrEmSettingsDetails());
		};
	}, [dispatch, emId]);

	useEffect(() => {
		if (!feeOptions) dispatch(getPpwrFeeOptionsRequest());
	}, [dispatch, feeOptions]);

	/** Settings of a previously opened EM may still be in the store until these load. */
	const view = useMemo<TPpwrEmSettingsView | null>(() => {
		if (!settings || settings.emId !== emId) return null;
		const fee = feeOptions?.find((item) => item.id === settings.subscriptionPlanId);
		return {
			...settings,
			subscriptionPlanName: fee?.planName ?? `Plan #${settings.subscriptionPlanId}`,
		};
	}, [settings, emId, feeOptions]);

	const handleBack = () => {
		if (savedUrl && savedUrl.startsWith(PATHS.PPWR_PAGE)) navigate(savedUrl);
		else navigate(PATHS.PPWR_PAGE);
	};

	return (
		<Box sx={{ padding: '0 20px 0 20px' }}>
			<EMManagementIdHeader savedUrl={savedUrl} emIdProp={emId} showBackButton={false} />
			<Divider sx={{ my: 1, borderColor: COLORS.PRIMARY }} />
			<Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
				<Tooltip title="Back to the EPR/PPWR list">
					<IconButton onClick={handleBack} color="primary" sx={{ p: 0 }}>
						<KeyboardBackspace />
					</IconButton>
				</Tooltip>
				<Typography variant="h5" color="primary">
					EPR/PPWR Settings
				</Typography>
			</Box>

			<EasParamsBox<TPpwrEmSettingsView>
				items={(view ?? {}) as TPpwrEmSettingsView}
				boxStructure={ppwrSettingsBoxStructure}
				columnNumber={2}
				editable={!!view}
				loading={settingsLoading}
				fontSize="s"
				editDialog={PpwrSettingsEditDialog}
			/>

			<Box sx={{ mt: 1.5 }}>
				<PackingProfile emId={emId} />
			</Box>

			<Box sx={{ mt: 1.5 }}>
				<CountryExclusions emId={emId} />
			</Box>
		</Box>
	);
};
